import {
  createColumnHelper,
  PaginationState,
  SortingState
} from '@tanstack/table-core';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { IoAlertCircle, IoCheckmarkCircle } from 'react-icons/io5';
import { PiListMagnifyingGlass } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { match } from 'ts-pattern';
import 'react-toastify/dist/ReactToastify.css';

import ModalSourceCreate from '@/app/evaluations/_modalSourceCreate/modalSourceCreate';
import ModalSourceShow from '@/app/evaluations/_modalSourceShow/modalSourceShow';
import { Button } from '@/components/button/button';
import { DeprecationToggle } from '@/components/deprecationToggle/deprecationToggle';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { useCreateSource, useSourcesSearch } from '@/hooks/queries/useSources';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { Source, SourceCreateReq, SOURCE_TYPE_DISPLAY_NAMES, SOURCE_TYPES, SourceTypeEnum } from '@/types/evals';
import { getResourceIcon, getResourceName } from '@/utils/sourceUtils';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import styles from './page.module.scss';

const columnHelper = createColumnHelper<Source>();
const SERVER_SORT_COLUMNS: Record<string, string> = { name: 'name', created_at: 'created_at' };

export default function EvaluationSourcesPage() {
  const navigate = useNavigate();
  const [ showSourceIndex, setShowSourceIndex ] = useState<number | undefined>();
  const [ showModalCreate, setShowModalCreate ] = useState<boolean>();
  const [ useLocalZone ] = useLocalZonePreference();
  const [ showDeprecated, setShowDeprecated ] = useState<boolean>( false );
  const [ sorting, setSorting ] = useState<SortingState>( [ { id: 'created_at', desc: true } ] );
  const [ pagination, setPagination ] = useState<PaginationState>( {
    pageIndex: 0,
    pageSize: 30
  } );
  const [ nameFilter, setNameFilter ] = useState( '' );
  const [ sourceTypeFilter, setSourceTypeFilter ] = useState<SourceTypeEnum | ''>( '' );
  const [ exampleTypeFilter, setExampleTypeFilter ] = useState( '' );
  const [ committedFilters, setCommittedFilters ] = useState<{
    name: string;
    sourceType: SourceTypeEnum | '';
    exampleType: string;
  }>( { name: '', sourceType: '', exampleType: '' } );

  const activeSort = sorting[0] as SortingState[0] | undefined;

  const { data, isLoading, isError, error } = useSourcesSearch( {
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    name: committedFilters.name || undefined,
    source_type: committedFilters.sourceType || undefined,
    example_type: committedFilters.exampleType || undefined,
    is_deprecated: showDeprecated ? undefined : false,
    sort_by: activeSort ? SERVER_SORT_COLUMNS[ activeSort.id ] : undefined,
    sort_order: activeSort ? ( activeSort.desc ? 'desc' : 'asc' ) : undefined,
  } );

  const pageItems = data?.items ?? [];
  const createSourceMutation = useCreateSource();

  /* HANDLERS */
  const onSourceCreate = ( args: SourceCreateReq ) => {
    createSourceMutation.mutate(
      args,
      {
        onSuccess: ( result ) => {
          console.log( 'source created', result );
          toast.success( 'Source created successfully' );
          setShowModalCreate( false );
        },
        onError: ( error ) => {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create source';
          console.error( errorMessage );
          toast.error( errorMessage );
        }
      }
    );
  };

  /* COLUMNS */
  const columns = useMemo( () => [
    columnHelper.display( {
      id: 'status',
      header: 'Status',
      size: 70,
      cell: info => {
        const source = info.row.original;
        return (
          <span className={ clsx( styles.source_status, styles[ source.status ] ) }>
            {
              source.status === 'active'
                ? <IoCheckmarkCircle />
                : <IoAlertCircle />
            }
          </span>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 7, styles: ColumnModEnums.CENTER } )
      }
    } ),
    columnHelper.accessor( 'name', {
      header: 'Name',
      size: 130,
      cell: info => {
        const source = info.row.original;
        return (
          <span>
            { info.getValue() }
            { source.is_deprecated && (
              <span className='deprecated_tag'>(deprecated)</span>
            ) }
          </span>
        );
      },
      enableSorting: true,
      meta: {
        classStr: buildColumnClassString( { width: 13, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'description', {
      header: 'Description',
      size: 160,
      cell: info => info.getValue() || '-',
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 16, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor(
      ( row ) => getResourceName( row ),
      {
        id: 'source',
        header: 'Source',
        size: 280,
        cell: info => {
          const source = info.row.original;
          const resourceIcon = getResourceIcon( source );
          const resourceName = info.getValue();

          return (
            <span className={ styles.source__resource }>
              <span>{ resourceIcon }</span>
              <span>{ resourceName }</span>
              <span>({ source.example_count })</span>
            </span>
          );
        },
        enableSorting: false,
        meta: {
          classStr: buildColumnClassString( { width: 28, styles: ColumnModEnums.NO_OVERFLOW } )
        }
      }
    ),
    columnHelper.accessor( 'example_type', {
      header: 'Example Type',
      size: 120,
      cell: info => info.getValue(),
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'latest_version_number', {
      header: 'Latest version',
      size: 110,
      cell: info => info.getValue(),
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 9, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'created_at', {
      header: 'Created',
      size: 100,
      cell: info => {
        return TimeFormatter( {
          sourceType: TimeSourceTypeEnum.ISO,
          value: info.getValue(),
          outputFormat: OutputFormatEnum.OBJECT,
          opts: { useLocalZone }
        } ).date;
      },
      enableSorting: true,
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.display( {
      id: 'actions',
      header: 'Actions',
      size: 80,
      cell: info => {
        const source = info.row.original;
        const rowIndex = info.row.index;

        return (
          <p className='table_actions'>
            <span
              className='table_actions_item'
              role="button"
              tabIndex={ 0 }
              onClick={ () => setShowSourceIndex( rowIndex ) }
              onKeyDown={ ( e ) => { if ( e.key === 'Enter' || e.key === ' ' ) setShowSourceIndex( rowIndex ); } }
              title={ `Preview source: ${ source.name }` }
              aria-label={ `Preview source: ${ source.name }` }
              data-testid={ `source-preview-${ source.id }` }
            >
              <PiListMagnifyingGlass aria-hidden="true" />
            </span>
          </p>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 7, styles: ColumnModEnums.CENTER } )
      }
    } )
  ], [ useLocalZone ] );

  const handleRowClick = useCallback( ( source: Source ) => {
    const params = new URLSearchParams( {
      sid: source.id,
      version: String( source.version )
    } );
    navigate( `/evaluations/source?${ params.toString() }` );
  }, [ navigate ] );

  const tableState = useMemo( () => ( {
    sorting,
    pagination
  } ), [ sorting, pagination ] );

  const table = useNativeTable<Source>( {
    data: pageItems,
    columns,
    state: tableState,
    onSortingChange: ( updater ) => {
      setSorting( updater );
      setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    rowCount: data?.total ?? 0
  } );

  const handleApplyFilters = useCallback( () => {
    setCommittedFilters( { name: nameFilter, sourceType: sourceTypeFilter, exampleType: exampleTypeFilter } );
    setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
  }, [ nameFilter, sourceTypeFilter, exampleTypeFilter ] );

  const isPending = createSourceMutation.isPending;

  const sourceTypeLabel = sourceTypeFilter
    ? SOURCE_TYPE_DISPLAY_NAMES[ sourceTypeFilter ]
    : 'All Source Types';

  const exampleTypeLabel = exampleTypeFilter === 'message'
    ? 'Message'
    : exampleTypeFilter === 'conversation'
      ? 'Conversation'
      : 'All Example Types';

  return (
    <div className='page'>
      <ToastContainer />
      <>
        {showModalCreate && (
          <ModalSourceCreate
            cbAction={ onSourceCreate }
            onCloseAction={ () => {
              if ( !isPending ) {
                setShowModalCreate( false );
              }
            } }
            isPending={ isPending }
          />
        )}
        {( showSourceIndex != undefined ) && (
          <ModalSourceShow
            source={ pageItems[ showSourceIndex ] }
            onCloseAction={ () => setShowSourceIndex( undefined ) }
          />
        )}

        <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
          <span className='info_name'>
            <Heading as="h2">Sources</Heading>
          </span>
          <span className='info_actions'>
            <DeprecationToggle
              entityNamePlural='sources'
              showDeprecated={ showDeprecated }
              onChange={ ( next ) => {
                setShowDeprecated( next );
                setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
              } }
            />
            <Button variant='primary' icon={ <Icon icon={ FiPlus } /> } data-testid="create-source-button" onClick={ () => setShowModalCreate( !showModalCreate ) }>Create Source</Button>
          </span>
        </div>

        {!isLoading && !isError && (
          <div className={ clsx( 'layout__row', 'pad', 'filters' ) }>
            <div className='filter_field'>
              <Input
                type='text'
                label='Filter by name'
                placeholder='Filter by name...'
                value={ nameFilter }
                onChange={ ( e ) => setNameFilter( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
              />
            </div>
            <div className='filter_field'>
              <Menu
                label="Source Type"
                value={ sourceTypeLabel }
                variant="select"
              >
                <MenuItem value="All Source Types" onClick={ () => setSourceTypeFilter( '' ) } />
                { Object.values( SOURCE_TYPES ).map( type => (
                  <MenuItem
                    key={ type }
                    value={ SOURCE_TYPE_DISPLAY_NAMES[ type ] }
                    onClick={ () => setSourceTypeFilter( type ) }
                  />
                ) ) }
              </Menu>
            </div>
            <div className='filter_field'>
              <Menu
                label="Example Type"
                value={ exampleTypeLabel }
                variant="select"
              >
                <MenuItem value="All Example Types" onClick={ () => setExampleTypeFilter( '' ) } />
                <MenuItem value="Message" onClick={ () => setExampleTypeFilter( 'message' ) } />
                <MenuItem value="Conversation" onClick={ () => setExampleTypeFilter( 'conversation' ) } />
              </Menu>
            </div>
            <Button
              variant='secondary'
              onClick={ () => {
                setNameFilter( '' );
                setSourceTypeFilter( '' );
                setExampleTypeFilter( '' );
                setCommittedFilters( { name: '', sourceType: '', exampleType: '' } );
                setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
              } }
              title="Clear all filters"
              data-testid="clear-filters-button"
            >
              Clear Filters
            </Button>
            <Button
              variant='primary'
              onClick={ handleApplyFilters }
              title="Apply filters"
              data-testid="apply-filters-button"
            >
              Apply Filters
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className={ clsx( 'layout__row', styles.layout__sources ) }>
            <Loader />
          </div>
        ) : isError ? (
          <div className={ clsx( 'layout__row', styles.layout__sources ) } style={ { padding: '20px', color: 'red' } }>
            Error loading sources: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        ) : (
          <div className={ styles.layout__sources } data-testid="sources-table-container">
            <NativeTable table={ table } tbodyTestId="table-body-sources" onRowClick={ handleRowClick } />
          </div>
        )}
        {isPending && (
          <div>
            <Loader />
          </div>
        )}
      </>
    </div>
  );
}
